// Copyright (c) Docugami, Inc. All rights reserved.

import { FluentTheme, IBreadcrumbItem } from "@fluentui/react";
import { initializeFileTypeIcons } from "@fluentui/react-file-type-icons";
import { AuthProvider, AuthProviderCallback, Client, Options } from "@microsoft/microsoft-graph-client";
import { Drive, DriveItem } from "@microsoft/microsoft-graph-types";
import { getExtension, getFileSizeStringForKb, supportedExtensions } from "components/file_management/file_helpers";
import { FilePickerProps } from "components/storage/StorageProviderFilePicker";
import dayjs from "dayjs";
import { Token } from "networking/Authentication";
import { ProviderId, GraphItem as StorageProviderGraphItem } from "networking/StorageProviders";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { GraphDrivePicker } from "./GraphDrivePicker";
import { GraphFolder, GraphItem, GraphItemPicker } from "./GraphItemPicker";
import { GraphNavigationItem, GraphNavigator } from "./GraphNavigator";
import { StorageProviderPickerCommands, StorageProviderPickerModal } from "./StorageProviderItemPickerModal";
import { ProviderPickerItem } from "./ProviderPickerItem";

const RootContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    min-height: 500px;
    gap: 5px;
`

const NavContainer = styled.div`
    display: flex;
    overflow-y: auto;
    min-height: 500px;
    background: ${FluentTheme.palette.neutralLighterAlt};
    border-radius: 10px 0px 0px 10px;
    min-width: 150px;
`

const ContentContainer = styled.div`
    display: flex;
    flex: 3;
    flex-direction: column;
    padding: 0px 5px 5px 0px;
    gap: 5px;
`

export enum GraphPickerSource {
    OneDrive = 'OneDrive',
    SharePoint = 'SharePoint'
}

interface NavigationState {
    navigationItem?: GraphNavigationItem;
    driveId?: string;
    folder?: GraphFolder;
    selectedItems?: GraphItem[];
}

interface GraphPickerProps extends FilePickerProps {
    defaultSource?: GraphPickerSource;
    sourceFilter?: GraphPickerSource[];
}

export const GraphPicker = (props: GraphPickerProps): JSX.Element => {
    const [navState, setNavState] = useState<NavigationState>({});
    const client = useRef<Client>(createClient(props.token));
    const currentSource = props.defaultSource ?? GraphPickerSource.OneDrive;

    useEffect(() => {
        ensureFontIconsInitialized();
    }, []);

    useEffect(() => {
        client.current = createClient(props.token);
    }, [props.token]);

    async function loadFolder(id?: string): Promise<void> {
        const driveId = navState.driveId!;
        const itemPart = !!id ? `items/${id}` : 'root';
        const url = `drives/${driveId}/${itemPart}`;

        await loadFolderByUrl(url);
    }

    async function loadFolderByPath(path?: string): Promise<void> {
        const driveId = navState.driveId!;
        const url = `drives/${driveId}/root:/${path ?? ''}`;

        await loadFolderByUrl(url);
    }

    async function loadFolderByUrl(url: string): Promise<GraphFolder> {
        const driveItem = (await client.current.api(url)
            .select('id,parentReference,name,webUrl')
            .expand('children($select=id,webUrl,name,folder,lastModifiedBy,fileSystemInfo,size)')
            .header("Cache-Control", "no-cache")
            .get()) as Partial<DriveItem>;

        const folder = parseFolder(driveItem);
        setNavState(x => {
            return { ...x, folder: folder, selectedItems: [] }
        });

        return folder;
    }

    async function onItemSelected(item: GraphNavigationItem) {
        if (item.siteId === navState.navigationItem?.siteId) {
            return;
        }

        const newState: NavigationState = {
            navigationItem: item
        };

        if (item.drives.length === 1) {
            // Only a single drive so we can select it by default
            newState.driveId = item.drives[0].id!;
        }

        setNavState(newState);
    }

    function onDriveSelected(drive: Partial<Drive>) {
        setNavState(x => { return { ...x, driveId: drive.id! } });
    }

    function getRootName() {
        if (!!navState.navigationItem?.isPersonal) {
            return "My Files"
        }
        else if (!!navState.navigationItem && !!navState.driveId) {
            return navState.navigationItem.drives.find(x => x.id! === navState.driveId)!.name!
        }

        return '';
    }

    function getBreadcrumbs(): IBreadcrumbItem[] {
        if (!navState.folder || !navState.navigationItem || !navState.driveId) {
            return [];
        }

        const crumbs: IBreadcrumbItem[] = [];
        const showDrivePicker = navState.navigationItem.drives.length > 1;
        if (showDrivePicker) {
            // Root drive picker breadcrumb
            crumbs.push({
                text: navState.navigationItem.name,
                key: navState.navigationItem.siteId,
                onClick: () => setNavState({ navigationItem: navState.navigationItem })
            });
        }

        // Push the root directory
        const folder = navState.folder;
        crumbs.push({
            text: getRootName(),
            key: navState.driveId!,
            onClick: () => loadFolder(),
            isCurrentItem: !folder.path
        });

        if (!!folder.path) {
            // Add all the child paths
            let names = folder.path.split('/');
            const rootIndex = names.indexOf('root:');
            if (rootIndex !== -1) {
                names = [...names.slice(rootIndex + 1), folder.name];
                crumbs.push(...names.map((name, index): IBreadcrumbItem => {
                    const relativePath = (names.slice(0, index + 1)).join('/');
                    return {
                        text: name,
                        key: `name_${index}`,
                        onClick: () => loadFolderByPath(relativePath),
                        isCurrentItem: index + 1 === names.length
                    }
                }))
            }
        }

        return crumbs;
    }

    function getPicker() {
        if (!!navState.navigationItem) {
            if (!!navState.driveId) {
                return <GraphItemPicker
                    folder={navState.folder}
                    loadFolder={loadFolder}
                    breadCrumbs={getBreadcrumbs()}
                    onSelectionChanged={items => setNavState(x => { return { ...x, selectedItems: items } })}
                    extensionFilter={supportedExtensions}
                    itemTypeFilter={props.itemTypeFilter}
                    selectionMode={props.selectionMode} />
            }
            else if (navState.navigationItem.drives.length > 1) {
                return <GraphDrivePicker title={"Document Libraries"} drives={navState.navigationItem?.drives ?? []} onSelected={onDriveSelected} />;
            }
        }

        return null;
    }

    function onConfirm() {
        if (!navState.selectedItems?.length) {
            return;
        }

        // Confirmed
        const items = navState.selectedItems!.map((item): ProviderPickerItem => {
            const graphItem: StorageProviderGraphItem = {
                id: {
                    driveId: navState.driveId!,
                    itemId: item.id!
                }
            };

            const providerId: ProviderId = currentSource === GraphPickerSource.OneDrive ? ProviderId.OneDrive : ProviderId.SharePoint;
            return {
                item: graphItem,
                path: getFullFilePath(item, navState.folder),
                name: item.name,
                isFolder: item.isFolder,
                providerId: providerId
            }
        });

        props.onConfirm(items);
    }

    return (
        <StorageProviderPickerModal isOpen={props.isOpen}>
            <RootContainer>
                <NavContainer>
                    <GraphNavigator client={client.current} onItemSelected={onItemSelected} defaultSource={currentSource} sourceFilter={props.sourceFilter} />
                </NavContainer>
                <ContentContainer>
                    {getPicker()}
                    <StorageProviderPickerCommands
                        primaryAction={{
                            label: 'Confirm',
                            disabled: !navState.selectedItems?.length,
                            onClick: onConfirm
                        }}
                        secondaryAction={{
                            label: 'Cancel',
                            onClick: props.onCancel
                        }} />
                </ContentContainer>
            </RootContainer>
        </StorageProviderPickerModal>
    )
}

function parseFolder(item: Partial<DriveItem>): GraphFolder {
    return {
        id: item.id!,
        name: item.name ?? "",
        webUrl: item.webUrl ?? '',
        parent: item.parentReference?.id ?? '',
        path: item.parentReference?.path ?? '',
        children: parseItems(item.children ?? [])
    }
}

function getFullFilePath(item: GraphItem, folder?: GraphFolder): string {
    let filePath: string = item.name;
    if (!!folder?.path) {
        // Add all the child paths
        let names = folder.path.split('/');
        const rootIndex = names.indexOf('root:');
        if (rootIndex !== -1) {
            names = [...names.slice(rootIndex + 1), folder.name];
        }

        filePath = `${names.join('/')}/${item.name}`;
    }

    return filePath;
}

function parseItems(items: DriveItem[]): GraphItem[] {
    return items.map(item => {
        const ext = getExtension(item.name!);
        const lastModifiedString = dayjs(new Date(item.fileSystemInfo!.lastModifiedDateTime!)).format('MMM D, YYYY');
        let size = '';
        if (!item.folder) {
            size = !!item.size ? `${getFileSizeStringForKb(Math.round(item.size / 1024))}` : '';
        }
        else {
            size = !!item.folder.childCount ? `${item.folder.childCount} item${item.folder.childCount > 1 ? 's' : ''}` : '0 items';
        }

        return {
            id: item.id!,
            lastModifiedBy: item.lastModifiedBy?.user?.displayName ?? '',
            lastModified: lastModifiedString,
            name: item.name ?? '',
            webUrl: item.webUrl ?? '',
            size: size,
            isFolder: !!item.folder,
            extension: !!ext ? ext : undefined
        };
    });
}

function createClient(token: Token): Client {
    const authProvider: AuthProvider = (callback: AuthProviderCallback) => {
        callback(null, token.value);
    }

    const options: Options = {
        authProvider: authProvider
    }

    const client = Client.init(options);
    return client;
}

let fontIconsIntialized = false;
function ensureFontIconsInitialized() {
    if (!fontIconsIntialized) {
        fontIconsIntialized = true;
        initializeFileTypeIcons();
    }
}
